// Formatting for the player dossier. Pure and DOM-free, so the interesting
// rules are unit-testable and the components stay presentational.
//
// A file of its own rather than an addition to lib/draftView.ts: that one is
// the home for the clock, the pool filter and pick ordering, and folding
// player-profile formatting in there makes both harder to find.

import type { NewsItem } from '../types/protocol'

// Height is stored as plain inches; people read feet-inches. Null rather than
// a dash because every player-facing surface omits a missing field entirely —
// the admin table is the one place that prints a placeholder, so it is the one
// caller that appends its own.
export function formatHeight(inches: number | null): string | null {
  if (inches === null) return null
  return `${Math.floor(inches / 12)}'${inches % 12}"`
}

// Sleeper's depth-chart slot isn't always the roster position (a WR can sit at
// LWR/SWR/PR), so show the slot and, when known, the order within it. Takes the
// two fields rather than a player so both AdminPlayer and PlayerSummary satisfy
// it without a shared interface.
export function depthChart(position: string | null, order: number | null): string | null {
  if (!position) return null
  if (order === null) return position
  return `${position} ${order}`
}

const MINUTE = 60_000
const HOUR = 60 * MINUTE
const DAY = 24 * HOUR

// "just now", "12m ago", "4h ago", "3d ago". `now` is injectable so tests need
// no fake timers.
//
// A negative elapsed time clamps to zero: a clock skewed slightly into the
// future is far more likely than genuinely future-dated news, and "in 2
// minutes" on a news item reads as a bug.
export function relativeTime(iso: string | null, now: number = Date.now()): string | null {
  if (!iso) return null
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return null

  const elapsed = Math.max(0, now - then)
  if (elapsed < MINUTE) return 'just now'
  if (elapsed < HOUR) return `${Math.floor(elapsed / MINUTE)}m ago`
  if (elapsed < DAY) return `${Math.floor(elapsed / HOUR)}h ago`
  return `${Math.floor(elapsed / DAY)}d ago`
}

export interface NormalizedNews {
  items: NewsItem[]
  unavailable: boolean
}

// api() returns `payload as T` unchecked and hands back null for a non-JSON
// body, so every news response is normalised here before it reaches React.
//
// `status: "ok"` with an empty list is NOT unavailable: Sleeper answers 200 []
// for a quiet week, a team defense and a nonsense id alike, so "we checked and
// there is nothing" is a normal state the UI has to be able to say. A
// truthiness check on `news` collapses it into "we could not reach Sleeper",
// which is the one distinction this whole section rests on.
export function normalizeNews(payload: unknown): NormalizedNews {
  const empty = { items: [] as NewsItem[], unavailable: true }
  if (payload === null || typeof payload !== 'object') return empty

  const { status, news } = payload as { status?: unknown; news?: unknown }
  if (status !== 'ok') return empty
  if (!Array.isArray(news)) return empty

  // Items are not re-validated element by element: the server has already
  // dropped malformed ones and nulled any non-https url, and a second copy of
  // that rule here would be a second place for it to rot.
  return { items: news as NewsItem[], unavailable: false }
}
